import dayjs from "dayjs"
import { Chip, Loader } from "~/ui-rtk/components/ui/common"
import {
  useListMegaFeedStructureRunsQuery,
  type MegaFeedRunStatus,
  type MegaFeedStructureRunSummary,
} from "~/ui-rtk/api/megaFeedStructureApi"

const RUN_STATUS_LABEL: Record<MegaFeedRunStatus, string> = {
  PENDING: "Queued",
  RUNNING: "Filling…",
  COMPLETED: "Completed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
}

const RUN_STATUS_STYLE: Record<MegaFeedRunStatus, string> = {
  PENDING: "text-cadet-blue",
  RUNNING: "text-basic-white",
  COMPLETED: "text-green-400",
  FAILED: "text-red-400",
  CANCELLED: "text-cadet-blue",
}

type Props = {
  structureId: string
  onOpen: (run: MegaFeedStructureRunSummary) => void
}

/** A structure's past runs, newest first; each opens its run grid. */
export default function StructureRunHistory({ structureId, onOpen }: Props) {
  const { data: runs = [], isLoading } =
    useListMegaFeedStructureRunsQuery(structureId)

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <div className="text-sm text-cadet-blue p-6 text-center border border-dashed border-basic-blue rounded-lg">
        No runs yet. Apply this structure to fill a feed.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-system-xs max-h-[55vh] overflow-y-auto">
      {runs.map(run => (
        <button
          key={run.id}
          type="button"
          onClick={() => onOpen(run)}
          className="bg-system-blue-800 hover:bg-system-blue-700 rounded-lg p-3 flex items-center gap-system-sm text-left"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-system-xs text-sm text-basic-white">
              {dayjs(run.createdAt).format("MMM D, YYYY HH:mm")}
              <span className={`text-xs ${RUN_STATUS_STYLE[run.status]}`}>
                {RUN_STATUS_LABEL[run.status]}
              </span>
              {run.appliedAt && (
                <Chip className="bg-green-700/40 text-green-300 text-xs">
                  Published {dayjs(run.appliedAt).format("MMM D")}
                </Chip>
              )}
            </div>
            <div className="text-xs text-cadet-blue mt-0.5">
              {run.refCount} products · {run.counts.MATCHED ?? 0} reused ·{" "}
              {run.counts.GENERATED ?? 0} generated · {run.counts.UNFILLED ?? 0}{" "}
              missing
              {run.counts.FAILED ? ` · ${run.counts.FAILED} failed` : ""}
            </div>
            {run.error && (
              <div className="text-xs text-red-400 mt-0.5 truncate">
                {run.error}
              </div>
            )}
          </div>
          <span className="text-xs text-cadet-blue shrink-0">View →</span>
        </button>
      ))}
    </div>
  )
}
